import { Eye } from "lucide-react";
import { t5 } from "./templateUtils";

const UNASSIGNED = "__unassigned__";

// Pivot every template's slots for one grid week into support-worker rows:
// { key, name, cells: { [day_offset]: [{ start_time, end_time, where }] } }.
// Workers sorted by name; unassigned slots collected in a last row.
export function workerRows(templates, week, staffName) {
  const byWorker = new Map();
  for (const t of templates) {
    const where = t.property ? (t.property_name || t.property) : (t.participant_name || t.participant);
    for (const s of t.slots || []) {
      if (Number(s.week) !== week) continue;
      const key = s.support_worker || UNASSIGNED;
      if (!byWorker.has(key)) {
        byWorker.set(key, { key, name: key === UNASSIGNED ? "Unassigned" : (staffName(key) || s.worker_name || key), cells: {} });
      }
      const row = byWorker.get(key);
      const off = Number(s.day_offset);
      (row.cells[off] = row.cells[off] || []).push({ start_time: s.start_time, end_time: s.end_time, where, sil: !!t.property });
    }
  }
  const rows = [...byWorker.values()];
  for (const r of rows) {
    for (const off of Object.keys(r.cells)) r.cells[off].sort((a, b) => t5(a.start_time).localeCompare(t5(b.start_time)));
  }
  return rows.sort((a, b) => (a.key === UNASSIGNED) - (b.key === UNASSIGNED)
    || a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

// Read-only: assignments are changed on the participant / SIL rows above.
export function TemplateWorkerView({ templates, week, DAYS, staffName }) {
  const rows = workerRows(templates, week, staffName);
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Support Workers <span className="text-slate-400 font-normal">({rows.filter((r) => r.key !== UNASSIGNED).length})</span>
        </h2>
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium px-2 py-0.5" data-testid="worker-view-readonly">
          <Eye className="h-3 w-3" />Read only
        </span>
      </div>
      <p className="text-[11px] text-slate-400 mb-1">Who is on each template in Week {week}. To change an assignment, edit the participant or SIL house above.</p>
      {rows.length === 0 ? <div className="text-xs text-slate-400 py-2">No shifts in Week {week}.</div> : (
        <div className="mt-2 border border-slate-200 rounded-lg overflow-auto max-h-[70vh] bg-white" data-testid="template-grid-support-worker">
          <div style={{ minWidth: 1100 }}>
            <div className="grid" style={{ gridTemplateColumns: "230px repeat(7, 1fr)" }}>
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 sticky left-0 top-0 z-30">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Support Worker</span>
              </div>
              {DAYS.map(([lb]) => <div key={lb} className="px-2 py-2 bg-slate-50 border-b border-l border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500 text-center sticky top-0 z-20">{lb}</div>)}
              {rows.map((r) => (
                <WorkerRow key={r.key} r={r} DAYS={DAYS} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkerRow({ r, DAYS }) {
  const unassigned = r.key === UNASSIGNED;
  return (
    <>
      <div className="px-3 py-2 border-b border-slate-100 sticky left-0 bg-white z-10" data-testid={`worker-row-${r.key}`}>
        <div className={`text-xs font-semibold truncate ${unassigned ? "text-amber-700" : "text-slate-800"}`}>{r.name}</div>
      </div>
      {DAYS.map(([, off]) => {
        const slots = r.cells[off] || [];
        return (
          <div key={off} className="px-1.5 py-2 border-b border-l border-slate-100 space-y-1" data-testid={`worker-cell-${r.key}-${off}`}>
            {slots.length === 0 ? <div className="text-[10px] text-slate-200 text-center">·</div> :
              slots.map((s, i) => (
                <div key={i} className={`rounded border px-1.5 py-1 ${unassigned ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="text-[10px] font-mono font-semibold text-slate-600">{t5(s.start_time)}–{t5(s.end_time)}</div>
                  <div className="text-[10px] text-slate-500 truncate" title={s.where}>{s.where}{s.sil ? " (SIL)" : ""}</div>
                </div>
              ))}
          </div>
        );
      })}
    </>
  );
}
